(function () {
  'use strict';

  function mountVideo(frame) {
    if (frame.dataset.pdpVideoMounted === '1') return frame.querySelector('video');
    var src = frame.dataset.videoSrc;
    if (!src) return null;
    frame.dataset.pdpVideoMounted = '1';

    var video = document.createElement('video');
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    /* Plays only on tap, so nothing is fetched up front when a poster covers the frame. */
    video.preload = frame.dataset.videoPoster ? 'none' : 'metadata';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');

    var poster = frame.dataset.videoPoster || '';
    if (poster) {
      /* Poster waits with the card's poster img until the first interaction (assets/interaction-media.js). */
      if (frame.querySelector('img[data-interaction-media]')) {
        document.addEventListener('theme:media:released', function () { video.poster = poster; }, { once: true });
      } else {
        video.poster = poster;
      }
    }

    var source = document.createElement('source');
    source.src = src;
    source.type = 'video/mp4';
    video.appendChild(source);

    /* Auto-hide the overlay controls 2s after a play/pause action; reveal
       them again on pointer interaction with the frame. */
    var hideTimer = null;
    function scheduleHideControls() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        frame.classList.add('controls-hidden');
      }, 2000);
    }
    function showControls() {
      clearTimeout(hideTimer);
      frame.classList.remove('controls-hidden');
    }

    video.addEventListener('playing', function () {
      frame.classList.add('is-playing');
      var btn = frame.querySelector('[data-video-play]');
      if (btn) btn.setAttribute('aria-label', 'Pause video');
      showControls();
      scheduleHideControls();
    });
    video.addEventListener('pause', function () {
      frame.classList.remove('is-playing');
      var btn = frame.querySelector('[data-video-play]');
      if (btn) btn.setAttribute('aria-label', 'Play video');
      showControls();
      scheduleHideControls();
    });

    /* Bring controls back when the user touches/hovers the video. */
    frame.addEventListener('pointerenter', showControls);
    frame.addEventListener('pointermove', function () {
      showControls();
      if (!video.paused) scheduleHideControls();
    });
    frame.addEventListener('pointerdown', function () {
      showControls();
      scheduleHideControls();
    });
    video.addEventListener('volumechange', function () {
      frame.classList.toggle('is-muted', video.muted);
      var btn = frame.querySelector('[data-video-mute]');
      if (btn) btn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
    });

    /* Seed muted class — video starts muted. */
    frame.classList.add('is-muted');

    var playBtn = frame.querySelector('[data-video-play]');
    if (playBtn) {
      playBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (video.paused) {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    var muteBtn = frame.querySelector('[data-video-mute]');
    if (muteBtn) {
      muteBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        video.muted = !video.muted;
      });
    }

    frame.appendChild(video);
    return video;
  }

  function initSection(section) {
    if (section.dataset.pdpStoriesInit === '1') return;
    section.dataset.pdpStoriesInit = '1';

    var carousel = section.querySelector('[data-pdp-stories-carousel]');
    var track = section.querySelector('[data-pdp-stories-track]');
    if (!carousel || !track) return;

    var originalSlides = Array.from(track.children);
    if (originalSlides.length === 0) return;

    var n = originalSlides.length;
    var isMobile = window.innerWidth < 750;
    var prefixCount = 0;

    /* ---- Infinite cloning — mobile only ---- */
    if (isMobile && n > 1) {
      prefixCount = n;
      /* Prepend n clones in original order (insertBefore reversal builds them correctly). */
      for (var i = n - 1; i >= 0; i--) {
        var pre = originalSlides[i].cloneNode(true);
        pre.classList.remove('is-active');
        pre.dataset.pdpClone = '1';
        pre.setAttribute('aria-hidden', 'true');
        track.insertBefore(pre, track.firstChild);
      }
      /* Append n clones. */
      for (var j = 0; j < n; j++) {
        var post = originalSlides[j].cloneNode(true);
        post.classList.remove('is-active');
        post.dataset.pdpClone = '1';
        post.setAttribute('aria-hidden', 'true');
        track.appendChild(post);
      }
    }

    var slides = Array.from(track.children);

    /* Mount videos on originals + clones. */
    slides.forEach(function (slide) {
      var frame = slide.querySelector('[data-video-frame]');
      if (frame) mountVideo(frame);
    });

    /* Pause videos that scroll out of view. No auto-resume — user taps play. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target.querySelector('video');
          if (!v) return;
          if (!entry.isIntersecting) v.pause();
        });
      }, { threshold: 0.1 });
      slides.forEach(function (s) { io.observe(s); });
    }

    /* Active card = whichever slide's centre is closest to the carousel centre. */
    var activeIdx = -1;
    function updateActive() {
      var rect = carousel.getBoundingClientRect();
      var carouselCentre = rect.left + rect.width / 2;
      var bestIdx = 0;
      var bestDist = Infinity;
      for (var k = 0; k < slides.length; k++) {
        var r = slides[k].getBoundingClientRect();
        var c = r.left + r.width / 2;
        var d = Math.abs(c - carouselCentre);
        if (d < bestDist) { bestDist = d; bestIdx = k; }
      }
      if (bestIdx !== activeIdx) {
        if (activeIdx >= 0) {
          slides[activeIdx].classList.remove('is-active');
          /* Swiped away from the previously-active card — pause its video. */
          var prevVideo = slides[activeIdx].querySelector('video');
          if (prevVideo && !prevVideo.paused) prevVideo.pause();
        }
        slides[bestIdx].classList.add('is-active');
        activeIdx = bestIdx;
      }
    }

    /* Silently jump from clone zone to the matching real slide. */
    var teleporting = false;
    function teleportIfNeeded() {
      if (!isMobile || n <= 1 || teleporting || activeIdx < 0) return;
      var targetIdx = -1;
      if (activeIdx < prefixCount) {
        targetIdx = activeIdx + n;                /* left clone → real */
      } else if (activeIdx >= prefixCount + n) {
        targetIdx = activeIdx - n;                /* right clone → real */
      }
      if (targetIdx < 0) return;

      teleporting = true;
      var targetSlide = slides[targetIdx];
      var targetPos = targetSlide.offsetLeft + targetSlide.offsetWidth / 2 - carousel.clientWidth / 2;

      /* Suppress the card's scale/opacity transition so the destination
         appears instantly at full size instead of animating in (the blink). */
      var targetCard = targetSlide.querySelector('.section-pdp-stories__card');
      if (targetCard) {
        targetCard.style.transition = 'none';
        void targetCard.offsetWidth; /* flush so the browser applies transition:none before the class change */
      }

      carousel.style.setProperty('scroll-snap-type', 'none', 'important');
      carousel.style.setProperty('scroll-behavior', 'auto', 'important');
      carousel.scrollLeft = targetPos;

      slides[activeIdx].classList.remove('is-active');
      slides[targetIdx].classList.add('is-active');
      activeIdx = targetIdx;

      requestAnimationFrame(function () {
        if (targetCard) targetCard.style.transition = '';
        carousel.style.removeProperty('scroll-snap-type');
        carousel.style.removeProperty('scroll-behavior');
        teleporting = false;
      });
    }

    var rafScroll = null;
    var scrollTimer = null;
    carousel.addEventListener('scroll', function () {
      if (teleporting) return;
      if (!rafScroll) {
        rafScroll = requestAnimationFrame(function () {
          rafScroll = null;
          updateActive();
        });
      }
      if (isMobile && n > 1) {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(teleportIfNeeded, 80);
      }
    }, { passive: true });

    /* Clicking an inactive slide smoothly centres it. */
    track.addEventListener('click', function (e) {
      var slide = e.target.closest('[data-pdp-stories-slide]');
      if (!slide) return;
      if (slide.classList.contains('is-active')) return;
      e.preventDefault();
      e.stopPropagation();
      var target = slide.offsetLeft + (slide.offsetWidth / 2) - (carousel.clientWidth / 2);
      try {
        carousel.scrollTo({ left: target, behavior: 'smooth' });
      } catch (err) {
        carousel.scrollLeft = target;
      }
    }, true);

    /* On resize, re-centre the active slide. */
    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        if (activeIdx >= 0) {
          var s = slides[activeIdx];
          carousel.scrollLeft = s.offsetLeft + (s.offsetWidth / 2) - (carousel.clientWidth / 2);
        }
        updateActive();
      });
    });

    /* Initial layout: centre the middle original slide.
       On mobile, originals start at prefixCount so the init index is offset. */
    requestAnimationFrame(function () {
      var initIdx = prefixCount + Math.floor(n / 2);
      var initSlide = slides[initIdx];
      if (initSlide) {
        carousel.scrollLeft = initSlide.offsetLeft + initSlide.offsetWidth / 2 - carousel.clientWidth / 2;
      }
      updateActive();
      requestAnimationFrame(function () {
        if (initSlide) {
          carousel.scrollLeft = initSlide.offsetLeft + initSlide.offsetWidth / 2 - carousel.clientWidth / 2;
        }
        updateActive();
        section.setAttribute('data-pdp-stories-ready', 'true');
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-pdp-stories]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('[data-pdp-stories]') : null;
    if (s) initSection(s);
  });
})();
